import React, { useState } from "react";
import { Link } from "react-router-dom";
import ProductRating from "./ProductRating";
import "../style/GameShowcasePage.css";

function ActionLink({ action, className }) {
  if (!action) {
    return null;
  }

  const Icon = action.icon;
  const content = (
    <>
      {Icon ? <Icon aria-hidden="true" /> : null}
      <span>{action.label}</span>
    </>
  );

  if (action.to) {
    return (
      <Link to={action.to} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <a
      href={action.href}
      className={className}
      target={action.external ? "_blank" : undefined}
      rel={action.external ? "noopener noreferrer" : undefined}
      download={action.download ? "" : undefined}
    >
      {content}
    </a>
  );
}

export default function GameShowcasePage({
  theme = "ghost",
  eyebrow,
  title,
  summary,
  productCode,
  productRating,
  cover,
  coverAlt,
  highlights = [],
  primaryAction,
  secondaryAction,
  storyTitle = "Dentro il gioco",
  storyParagraphs = [],
  features = [],
  facts = [],
  mediaCard = null,
  leaderboardLabel = "Classifica",
  leaderboardPanel = null
}) {
  const hasLeaderboard = Boolean(leaderboardPanel);
  const [activePanel, setActivePanel] = useState("overview");
  const tabs = hasLeaderboard
    ? [
        { id: "overview", label: "Panoramica" },
        { id: "leaderboard", label: leaderboardLabel }
      ]
    : [{ id: "overview", label: "Panoramica" }];

  return (
    <section className={`game-showcase game-showcase--${theme}`}>
      <div className="game-showcase__hero">
        <div className="game-showcase__cover-wrap">
          <div className="game-showcase__cover-glow" aria-hidden="true" />
          <img src={cover} alt={coverAlt} className="game-showcase__cover" />
        </div>

        <div className="game-showcase__hero-copy">
          {eyebrow ? <p className="game-showcase__eyebrow">{eyebrow}</p> : null}
          <h1>{title}</h1>
          <p className="game-showcase__summary">{summary}</p>
          {productCode ? (
            <ProductRating productCode={productCode} productName={title} rating={productRating} />
          ) : null}

          {highlights.length > 0 ? (
            <ul className="game-showcase__highlights" aria-label={`Punti chiave di ${title}`}>
              {highlights.map(highlight => (
                <li key={highlight}>{highlight}</li>
              ))}
            </ul>
          ) : null}

          {(primaryAction || secondaryAction) && (
            <div className="game-showcase__actions">
              <ActionLink
                action={primaryAction}
                className="game-showcase__action game-showcase__action--primary"
              />
              <ActionLink
                action={secondaryAction}
                className="game-showcase__action game-showcase__action--secondary"
              />
            </div>
          )}
        </div>
      </div>

      <div className="game-showcase__selector" role="tablist" aria-label={`Sezioni ${title}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activePanel === tab.id}
            className={`game-showcase__selector-button${
              activePanel === tab.id ? " game-showcase__selector-button--active" : ""
            }`}
            onClick={() => setActivePanel(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activePanel === "overview" ? (
        <div className="game-showcase__overview">
          <article className="game-showcase__story-card">
            <div className="game-showcase__section-heading">
              <p className="game-showcase__section-kicker">Descrizione</p>
              <h2>{storyTitle}</h2>
            </div>

            <div className="game-showcase__story-body">
              {storyParagraphs.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>

            {features.length > 0 ? (
              <div className="game-showcase__feature-grid">
                {features.map(feature => {
                  const Icon = feature.icon;
                  return (
                    <article key={feature.title} className="game-showcase__feature-card">
                      <div className="game-showcase__feature-icon" aria-hidden="true">
                        {Icon ? <Icon /> : null}
                      </div>
                      <div>
                        <h3>{feature.title}</h3>
                        <p>{feature.description}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </article>

          <aside className="game-showcase__sidebar">
            {mediaCard}

            {facts.length > 0 ? (
              <section className="game-showcase__fact-card">
                <p className="game-showcase__section-kicker">A colpo d'occhio</p>
                <h2>Come si presenta</h2>
                <dl className="game-showcase__fact-list">
                  {facts.map(fact => (
                    <div key={fact.label} className="game-showcase__fact-item">
                      <dt>{fact.label}</dt>
                      <dd>{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}
          </aside>
        </div>
      ) : (
        <div className="game-showcase__leaderboard-panel">{leaderboardPanel}</div>
      )}
    </section>
  );
}
